import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { apiFetch } from '@/lib/api-fetch';

type Platform = "twitter" | "linkedin" | "reddit" | "instagram";

interface AppState {
  sessionId: string;
  currentIdea: string;
  selectedPlatform: Platform;
  displayName: string | null;
  hasCompletedOnboarding: boolean;

  authToken: string | null;
  userId: string | null;
  userEmail: string | null;
  isEmailVerified: boolean;
  hasAccount: boolean;
  postsGeneratedCount: number;
  accountPromptDismissed: boolean;
  verificationBannerDismissed: boolean;
  isOnboarding: boolean;

  capturedEmail: string | null;

  isSubscribed: boolean;
  subscriptionStatus: string;
  subscriptionPlan: string | null;
  subscriptionPeriodEnd: string | null;

  loginCount: number;
  onboardingDismissed: boolean;

  setIdea: (idea: string) => void;
  setPlatform: (platform: Platform) => void;
  setDisplayName: (name: string) => void;
  completeOnboarding: () => void;
  resetSession: () => void;
  incrementPostsGenerated: () => void;
  dismissAccountPrompt: () => void;
  dismissVerificationBanner: () => void;
  setCapturedEmail: (email: string) => void;
  incrementLoginCount: () => void;
  dismissOnboardingBanner: () => void;

  loginUser: (data: {
    token: string;
    userId: string;
    email: string;
    emailVerified: boolean;
    canonicalSessionId: string;
    displayName?: string | null;
    subscriptionStatus?: string | null;
    subscriptionPlan?: string | null;
    subscriptionPeriodEnd?: string | null;
  }) => void;
  logoutUser: () => void;
  setEmailVerified: (data: { token: string; userId: string; email: string; canonicalSessionId: string; displayName?: string | null }) => void;
  startOnboarding: () => void;

  refreshAuthToken: (token: string) => void;
  setSubscription: (data: {
    subscriptionStatus: string;
    subscriptionPlan: string | null;
    subscriptionPeriodEnd: string | null;
  }) => void;
  fetchSubscription: () => Promise<void>;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      sessionId: uuidv4(),
      currentIdea: "",
      selectedPlatform: "twitter",
      displayName: null,
      hasCompletedOnboarding: false,

      authToken: null,
      userId: null,
      userEmail: null,
      isEmailVerified: false,
      hasAccount: false,
      postsGeneratedCount: 0,
      accountPromptDismissed: false,
      verificationBannerDismissed: false,
      isOnboarding: false,

      capturedEmail: null,

      isSubscribed: false,
      subscriptionStatus: "none",
      subscriptionPlan: null,
      subscriptionPeriodEnd: null,

      loginCount: 0,
      onboardingDismissed: false,

      setIdea: (idea) => set({ currentIdea: idea }),
      setPlatform: (platform) => set({ selectedPlatform: platform }),
      setDisplayName: (name) => set({ displayName: name || null }),
      completeOnboarding: () => set({ hasCompletedOnboarding: true }),
      resetSession: () => set({ sessionId: uuidv4(), currentIdea: "", selectedPlatform: "twitter" }),
      incrementPostsGenerated: () => set((s) => ({
        postsGeneratedCount: s.postsGeneratedCount + 1,
        accountPromptDismissed: s.hasAccount ? s.accountPromptDismissed : false,
      })),
      dismissAccountPrompt: () => set({ accountPromptDismissed: true }),
      dismissVerificationBanner: () => set({ verificationBannerDismissed: true }),
      setCapturedEmail: (email) => set({ capturedEmail: email }),
      incrementLoginCount: () => set((s) => ({ loginCount: s.loginCount + 1 })),
      dismissOnboardingBanner: () => set({ onboardingDismissed: true }),

      loginUser: ({ token, userId, email, emailVerified, canonicalSessionId, displayName, subscriptionStatus, subscriptionPlan, subscriptionPeriodEnd }) => {
        const subStatus = subscriptionStatus ?? "none";
        const subIsActive = subStatus === "active" || subStatus === "trialing";
        set({
          authToken: token,
          userId,
          userEmail: email,
          isEmailVerified: emailVerified,
          hasAccount: true,
          sessionId: canonicalSessionId,
          displayName: displayName || undefined,
          subscriptionStatus: subStatus,
          subscriptionPlan: subscriptionPlan ?? null,
          subscriptionPeriodEnd: subscriptionPeriodEnd ?? null,
          isSubscribed: subIsActive,
        });
      },

      logoutUser: () =>
        set({
          authToken: null,
          userId: null,
          userEmail: null,
          isEmailVerified: false,
          postsGeneratedCount: 0,
          accountPromptDismissed: false,
          verificationBannerDismissed: false,
          isSubscribed: false,
          subscriptionStatus: "none",
          subscriptionPlan: null,
          subscriptionPeriodEnd: null,
        }),

      setEmailVerified: ({ token, userId, email, canonicalSessionId, displayName }) =>
        set({
          isEmailVerified: true,
          authToken: token,
          userId,
          userEmail: email,
          hasAccount: true,
          sessionId: canonicalSessionId,
          displayName: displayName || undefined,
          verificationBannerDismissed: false,
        }),

      startOnboarding: () => set({ isOnboarding: true, hasCompletedOnboarding: false, hasAccount: false, authToken: null }),

      refreshAuthToken: (token) => set({ authToken: token }),

      setSubscription: ({ subscriptionStatus, subscriptionPlan, subscriptionPeriodEnd }) =>
        set({
          subscriptionStatus,
          subscriptionPlan,
          subscriptionPeriodEnd,
          isSubscribed: subscriptionStatus === "active" || subscriptionStatus === "trialing",
        }),

      fetchSubscription: async () => {
        const { authToken } = get();
        if (!authToken) return;

        try {
          const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
          const res = await apiFetch(`${baseUrl}/api/payments/subscription`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (res.status === 401) {
            try {
              const body = await res.clone().json();
              const msg: string = body?.error ?? "";
              if (msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("expired")) {
                get().logoutUser();
              }
            } catch {
            }
            return;
          }
          if (!res.ok) {
            set({
              isSubscribed: false,
              subscriptionStatus: "none",
              subscriptionPlan: null,
              subscriptionPeriodEnd: null,
            });
            return;
          }
          const data = await res.json();
          set({
            subscriptionStatus: data.subscriptionStatus || "none",
            subscriptionPlan: data.subscriptionPlan || null,
            subscriptionPeriodEnd: data.subscriptionPeriodEnd || null,
            isSubscribed: data.subscriptionStatus === "active" || data.subscriptionStatus === "trialing",
          });
        } catch {
          set({
            isSubscribed: false,
            subscriptionStatus: "none",
            subscriptionPlan: null,
            subscriptionPeriodEnd: null,
          });
        }
      },
    }),
    {
      name: 'scribpost-storage',
      partialize: (state) => {
        const { verificationBannerDismissed, isOnboarding, ...persisted } = state;
        return persisted;
      },
    }
  )
);
